import api from "@/assets/js/utils/api";
import * as swal from "@/assets/js/utils/alertService";
import router from "@/router";

export default {
  name: "LineLoginCallback",
  data() {
    return {
      // 狀態管理
      isLoading: true,
      isSuccess: false,
      isError: false,

      // 設定文字
      pageTitle: "LINE 登入處理",
      appName: "我的應用程式",
      processingMessage: "正在處理 LINE 登入...",
      successMessage: "登入成功！歡迎使用",
      errorMessage: "登入失敗，請重試",
      continueButtonText: "繼續使用",

      // 回調資料
      callbackData: {
        code: null,
        state: null,
        error: null,
        error_description: null,
      },

      // 用戶資訊
      userInfo: null,

      // 錯誤資料
      errorData: {
        code: "UNKNOWN",
        description: "請檢查您的網路連線或稍後再試",
      },

      // 授權時間
      authTime: "",
    };
  },

  computed: {
    showActions() {
      return this.isSuccess || this.isError;
    },
  },

  mounted() {
    this.parseUrlParams();
    this.processCallback();

    // ✅ 清除網址參數，避免重新整理重複 callback
    window.history.replaceState({}, document.title, window.location.pathname);
  },

  methods: {
    // 解析 URL 參數
    parseUrlParams() {
      const urlParams = new URLSearchParams(window.location.search);
      this.callbackData = {
        code: urlParams.get("code"),
        state: urlParams.get("state"),
        error: urlParams.get("error"),
        error_description: urlParams.get("error_description"),
      };
    
    },

    // ✅ 處理 LINE 授權回調 - 修正版
    async processCallback() {
      const { code, state, error } = this.callbackData;

      // ⚠️ 有錯誤參數直接顯示錯誤
      if (error) {
        await this.handleError(
          "LINE_ERROR",
          this.callbackData.error_description
        );
        return;
      }

      // ⚠️ 缺少必要參數
      if (!code || !state) {
        await this.handleError("INVALID_REQUEST", "缺少必要參數 code 或 state");
        return;
      }

      try {
        this.isLoading = true;

        // ⭐ 根據 state 決定正確的 API 路徑
        const apiPath = state.startsWith("login_")
          ? "/oauth/line/login" // LINE 登入專用 API
          : "/oauth/line/callback"; // LINE 綁定 API

       
        const { data } = await api.get(
          `${apiPath}?code=${code}&state=${state}`
        );

      
        this.isLoading = false;

        // ✅ 成功邏輯
        if (data.success) {
          this.isSuccess = true;
          this.userInfo = data.data;
          this.authTime = new Date().toLocaleString("zh-TW");

          // 🔹 通知父視窗成功
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              {
                source: "line-callback",
                success: true,
                message: state.startsWith("login_")
                  ? "LINE 登入成功，歡迎回來！"
                  : data.message || "LINE 綁定成功！",
              },
              "*"
            );

            setTimeout(() => window.close(), 500);
            return;
          }

          // ✅ 主視窗模式才顯示
          if (state.startsWith("bind_")) {
            await swal.showSuccess(data.message || "LINE 綁定成功！");
            router.push("/dashboard/profile");
          } else if (state.startsWith("login_")) {
            await swal.showSuccess("登入成功，歡迎回來！");
            router.push("/dashboard");
          }
        }

        // ❌ 失敗邏輯 - 修正版
        else {
          await this.handleApiError(data, state);
        }
      } catch (err) {
        console.error("LINE callback 發生錯誤：", err);
        await this.handleCatchError(err, state);
      }
    },

    // ✅ 處理 API 回傳失敗
    async handleApiError(data, state) {
      this.isError = true;

      // 📌 偵測重複綁定錯誤
      const isDuplicateBinding =
        data?.code === 409 ||
        data?.message?.includes("已被綁定") ||
        data?.message?.includes("already bound");

      const errorMessage = isDuplicateBinding
        ? "此 LINE 帳號已被綁定。"
        : data.message || "操作失敗，請稍後再試。";

      // 🔹 若有父視窗，通知父視窗
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            source: "line-callback",
            success: false,
            message: errorMessage,
          },
          "*"
        );

        setTimeout(() => window.close(), 500);
        return;
      }

      // 🔹 主視窗：必須顯示錯誤訊息
      await swal.showError(errorMessage);

      // 導向頁面
      if (state && state.startsWith("bind_")) {
        router.push("/dashboard/profile");
      } else {
        router.push("/login");
      }
    },

    // ✅ 處理 try-catch 錯誤
    async handleCatchError(err, state) {
      this.isLoading = false;
      this.isError = true;

      const backendMsg = err.response?.data?.message;
      const statusCode = err.response?.status;

      console.error("詳細錯誤資訊:", {
        status: statusCode,
        message: backendMsg,
        data: err.response?.data,
      });

      // 📌 偵測重複綁定錯誤
      const isDuplicateBinding =
        statusCode === 409 ||
        backendMsg?.includes("已被綁定") ||
        backendMsg?.includes("already bound");

      const errorMessage =
        backendMsg ||
        (isDuplicateBinding
          ? "此 LINE 帳號已被綁定。"
          : "伺服器發生錯誤，請稍後再試。");

      // ✅ 有父視窗情況：統一交給父視窗顯示
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            source: "line-callback",
            success: false,
            message: errorMessage,
          },
          "*"
        );

        setTimeout(() => window.close(), 500);
        return;
      }

      // ✅ 主視窗情況：必須顯示錯誤訊息
      await swal.showError(errorMessage);

      // 導頁
      if (state && state.startsWith("bind_")) {
        router.push("/dashboard/profile");
      } else {
        router.push("/login");
      }
    },

    // ✅ 統一的錯誤處理函數
    async handleError(errorCode, errorDesc) {
      this.isLoading = false;
      this.isError = true;

      const description = errorDesc || this.getErrorDescription(errorCode);

      this.errorData = {
        code: errorCode,
        description: description,
      };

      console.error("LINE 錯誤:", { errorCode, description });

      // 🔹 如有父視窗，通知父視窗
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          {
            source: "line-callback",
            success: false,
            message: description,
          },
          "*"
        );

        setTimeout(() => window.close(), 500);
        return;
      }

      // 🔹 主視窗：顯示錯誤訊息
      await swal.showError(description);
      router.push("/login");
    },

    // 顯示成功狀態
    showSuccess() {
      this.isLoading = false;
      this.isSuccess = true;

      // 模擬用戶資料
      this.userInfo = {
        name: "LINE 用戶",
        id: "U" + Math.random().toString(36).substr(2, 9),
        avatar: "👤",
      };

      this.authTime = new Date().toLocaleString("zh-TW");
    },

    // 顯示錯誤狀態
    showError(errorCode = null, errorDesc = null) {
      this.isLoading = false;
      this.isError = true;

      const code = errorCode || this.callbackData.error || "UNKNOWN_ERROR";
      const description =
        errorDesc ||
        this.callbackData.error_description ||
        this.getErrorDescription(code);

      this.errorData = {
        code: code,
        description: description,
      };
    },

    // 獲取錯誤描述
    getErrorDescription(errorCode) {
      const errorMessages = {
        access_denied: "用戶拒絕了授權請求",
        invalid_request: "請求參數無效或缺少必要參數",
        unauthorized_client: "客戶端未經授權執行此操作",
        unsupported_response_type: "不支援的回應類型",
        invalid_scope: "無效的授權範圍",
        server_error: "LINE 伺服器發生內部錯誤",
        temporarily_unavailable: "LINE 服務暫時無法使用",
        invalid_grant: "授權碼無效或已過期",
        LINE_ERROR: "LINE 授權發生錯誤",
        INVALID_REQUEST: "請求無效",
      };

      return errorMessages[errorCode] || "發生未知錯誤，請稍後再試";
    },

    // 繼續到應用程式
    continueToApp() {
      this.$emit("login-success", {
        userInfo: this.userInfo,
        authData: this.callbackData,
      });
    },

    // 重試登入
    retryLogin() {
      this.$emit("retry-login");
    },

    // 顯示訊息提示
    showMessage(message, type) {
      console.log(`${type}: ${message}`);
    },
  },
};
